import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Film,
  Music,
  BookOpen,
  ClipboardCheck,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Mail,
  Loader2,
  ArrowRight,
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function AccademiaLandingPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    cellulare: "",
    citta: "",
    centro_estetico: "",
    codice_distributore: "",
    note: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email) {
      toast.error("Compila Nome, Cognome ed Email");
      return;
    }
    if (!gdpr) {
      toast.error("Devi accettare l'informativa privacy");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/public/accademia/iscrizione`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        toast.success("Iscrizione inviata!");
      } else {
        const msg = data.detail || "Errore durante l'iscrizione. Riprova.";
        const isAlreadyExists = res.status === 400 && /già/i.test(msg);
        if (isAlreadyExists) {
          toast(msg, {
            duration: 12000,
            icon: "ℹ️",
            action: {
              label: "Vai al Login",
              onClick: () => { window.location.href = "/login"; },
            },
          });
        } else {
          toast.error(msg);
        }
      }
    } catch (err) {
      console.error("Iscrizione Accademia - errore:", err);
      toast.error("Errore di connessione. Verifica la rete e riprova.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#FAF6F2] flex items-center justify-center px-4 py-12" data-testid="accademia-success-page">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl border border-rose-100 p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#7A4856] to-[#5A3340] flex items-center justify-center mb-6 shadow-lg">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-light text-gray-800 mb-2" style={{ fontFamily: "Georgia, serif" }}>
            Iscrizione ricevuta
          </h1>
          <p className="text-gray-600 leading-relaxed mb-6">
            Grazie <strong>{form.nome}</strong>! La tua richiesta è stata registrata.
            <br />
            Verrai contattato/a all'indirizzo <strong className="text-[#7A4856]">{form.email}</strong> quando il tuo accesso all'Accademia sarà approvato.
          </p>
          <div className="bg-rose-50/60 border border-rose-100 rounded-2xl p-4 text-left text-sm text-gray-600 mb-6">
            <p className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#7A4856]" /> Cosa succede ora?
            </p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Il nostro team verifica la tua iscrizione</li>
              <li>Riceverai un'email con il link "Imposta password"</li>
              <li>Imposti la tua password e accedi all'area riservata</li>
            </ol>
          </div>
          <Link to="/">
            <Button variant="outline" className="rounded-full">Torna alla Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F2]" data-testid="accademia-landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3A1F2A] via-[#5A3340] to-[#7A4856]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 25% 30%, rgba(255,255,255,.7) 0, transparent 40%), radial-gradient(circle at 75% 70%, rgba(255,210,210,.6) 0, transparent 35%)"
        }} />
        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-2xl shrink-0">
              <img
                src="/odr-assets/logo_odr_accademia.jpg"
                alt="Accademia ODR ITA 318"
                className="h-28 sm:h-32 w-auto object-contain"
              />
            </div>
            <div className="flex-1 text-center lg:text-left">
              <p className="text-[#F5C9CD] text-xs uppercase tracking-[0.3em] mb-3 font-medium">
                · Formazione Scientifica Ufficiale ·
              </p>
              <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-light leading-tight" style={{ fontFamily: "Georgia, serif" }}>
                <span className="block">Accademia ODR</span>
                <span className="block italic font-extralight text-[#FAD9DC]">La Bellezza che Cura</span>
              </h1>
              <div className="h-px w-24 bg-[#F5C9CD] my-5 mx-auto lg:mx-0" />
              <p className="text-[#FAE6E8] text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                Spazio formativo riservato agli operatori partner della linea
                <em> Oncosmetics Derma Ritual</em>. Iscriviti per accedere a videocorsi,
                podcast scientifici e dispense tecniche basate sui meccanismi d'azione della Ioxina.
              </p>
              <div className="flex flex-wrap gap-3 justify-center lg:justify-start mt-6">
                <a href="#iscrizione">
                  <Button className="rounded-full bg-white text-[#5A3340] hover:bg-rose-50 px-6 shadow-lg" data-testid="cta-iscriviti">
                    Iscriviti ora <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/login">
                  <Button variant="outline" className="rounded-full border-white text-white bg-transparent hover:bg-white/10 hover:text-white px-6" data-testid="cta-login">
                    Hai già accesso? Login
                  </Button>
                </Link>
                <Link to="/academy-benessere-integrato">
                  <Button variant="outline" className="rounded-full border-[#F5C9CD] text-[#FAE6E8] bg-transparent hover:bg-white/10 hover:text-white px-6" data-testid="cta-academy-benessere">
                    Scopri l'Academy del Benessere <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COSA TROVI */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <p className="text-[#7A4856] text-xs uppercase tracking-[0.3em] mb-2">Cosa troverai</p>
          <h2 className="text-3xl font-light text-gray-800" style={{ fontFamily: "Georgia, serif" }}>
            Tre pilastri della tua formazione
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { Icon: Film, title: "Videocorsi", desc: "Formazione operativa sull'utilizzo della linea ODR. Da 1 a 130 minuti, ogni video con titolo, copertina e descrizione." },
            { Icon: Music, title: "Podcast Scientifici", desc: "Approfondimenti sui meccanismi d'azione della Ioxina e sull'approccio scientifico ODR." },
            { Icon: BookOpen, title: "Dispense Tecniche", desc: "Materiale formativo da consultare online. Al termine: test di comprensione automatico." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="bg-white rounded-2xl p-6 border border-rose-100/60 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F5DCDF] to-[#E8C4C9] flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-[#7A4856]" />
              </div>
              <h3 className="font-semibold text-gray-800 text-lg mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="bg-white border-y border-rose-100/60 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-[#7A4856] text-xs uppercase tracking-[0.3em] mb-2">Come funziona</p>
            <h2 className="text-3xl font-light text-gray-800" style={{ fontFamily: "Georgia, serif" }}>
              In 3 semplici passi
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "1", title: "Compila il modulo", desc: "Inserisci i tuoi dati. Il codice distributore è opzionale." },
              { n: "2", title: "Riceviamo la richiesta", desc: "Il team ODR verifica e approva il tuo accesso. Riceverai un'email." },
              { n: "3", title: "Imposta la password", desc: "Clicca il link nell'email, scegli la password e accedi alla Biblioteca Corsi." },
            ].map((step) => (
              <div key={step.n} className="text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#7A4856] to-[#5A3340] text-white text-2xl font-light flex items-center justify-center mb-4 shadow-md" style={{ fontFamily: "Georgia, serif" }}>
                  {step.n}
                </div>
                <h3 className="font-semibold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM ISCRIZIONE */}
      <section id="iscrizione" className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="bg-white rounded-3xl shadow-xl border border-rose-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#7A4856] to-[#5A3340] text-white px-6 sm:px-8 py-6">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-[#F5C9CD]" />
              <div>
                <h2 className="text-2xl font-light" style={{ fontFamily: "Georgia, serif" }}>
                  Iscriviti all'Accademia
                </h2>
                <p className="text-[#F5C9CD] text-sm mt-0.5">L'iscrizione è riservata ai partner ODR. La password verrà impostata via email dopo l'approvazione.</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="p-6 sm:p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="nome">Nome *</Label>
                <Input id="nome" value={form.nome} onChange={set("nome")} required className="rounded-xl" data-testid="iscrizione-nome" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input id="cognome" value={form.cognome} onChange={set("cognome")} required className="rounded-xl" data-testid="iscrizione-cognome" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required className="rounded-xl" data-testid="iscrizione-email" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cellulare">Cellulare</Label>
                <Input id="cellulare" value={form.cellulare} onChange={set("cellulare")} placeholder="+39 ..." className="rounded-xl" data-testid="iscrizione-cellulare" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="citta">Città</Label>
                <Input id="citta" value={form.citta} onChange={set("citta")} className="rounded-xl" data-testid="iscrizione-citta" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="centro_estetico">Centro estetico / Salone</Label>
              <Input id="centro_estetico" value={form.centro_estetico} onChange={set("centro_estetico")} placeholder="Nome dell'attività (opzionale)" className="rounded-xl" data-testid="iscrizione-centro" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="codice_distributore" className="flex items-center gap-2">
                Codice Distributore
                <span className="text-xs text-gray-400 font-normal italic">(opzionale)</span>
              </Label>
              <Input id="codice_distributore" value={form.codice_distributore} onChange={set("codice_distributore")} placeholder="es. ODR-2026-XXXX" className="rounded-xl" data-testid="iscrizione-codice" />
              <p className="text-xs text-gray-500">Se ti è stato fornito da un distributore o agente ODR, inseriscilo qui.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="note">Note</Label>
              <Textarea id="note" value={form.note} onChange={set("note")} rows={2} placeholder="Vuoi dirci qualcosa? (opzionale)" className="rounded-xl" data-testid="iscrizione-note" />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox id="gdpr" checked={gdpr} onCheckedChange={setGdpr} className="mt-0.5" data-testid="iscrizione-gdpr" />
              <label htmlFor="gdpr" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                Acconsento al trattamento dei miei dati personali per la finalità di iscrizione all'Accademia ODR, ai sensi del Reg. UE 2016/679 (GDPR). I dati sono gestiti da Swiss318 / ITA 318.
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-gradient-to-r from-[#7A4856] to-[#5A3340] hover:from-[#5A3340] hover:to-[#3A1F2A] text-white shadow-lg h-12 text-base"
              data-testid="iscrizione-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Invio in corso...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Invia richiesta di iscrizione
                </span>
              )}
            </Button>

            <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
              <ClipboardCheck className="w-3 h-3" /> Riservato ai partner ODR · Approvazione manuale
            </div>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Hai già un account?{" "}
          <Link to="/login" className="text-[#7A4856] hover:underline font-medium">
            Accedi qui
          </Link>
        </p>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#3A1F2A] text-white/70 py-8 px-4 text-center text-sm">
        <p style={{ fontFamily: "Georgia, serif" }} className="italic text-white mb-1">La Bellezza che Cura</p>
        <p>Accademia ODR · Swiss318 · ITA 318</p>
      </footer>
    </div>
  );
}
